import React, { Suspense } from 'react';
import { Navigate, redirect } from 'react-router-dom';
import axiosAdmin from '@/shared/api/instance/axiosAdmin';
import type { TokenValidationResponse } from '../shared/types/auth';
import { getAuthToken } from '../shared/utils/cookie';

// 로딩 컴포넌트
function PageLoading() {
  return (
    <div className='flex items-center justify-center h-screen'>
      <div className='text-lg'>페이지를 불러오는 중...</div>
    </div>
  );
}

// Lazy load 컴포넌트들
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const RegisterPage = React.lazy(() => import('../pages/register/RegisterPage'));

// Helper function to wrap lazy components with Suspense
const createLazyElement = (
  LazyComponent: React.LazyExoticComponent<React.ComponentType<any>>,
  props?: any
) =>
  React.createElement(
    Suspense,
    { fallback: React.createElement(PageLoading) },
    React.createElement(LazyComponent, props)
  );

export const registerLoader = async ({ request }: { request: Request }) => {
  const url = new URL(request.url);
  const token = url.searchParams.get('token');

  if (!token) {
    return redirect('/login');
  }

  try {
    // 쿼리 파라미터 방식으로 토큰 검증 (Bearer 방식이 아님)
    const response = await axiosAdmin.get<ApiResponse<TokenValidationResponse>>(
      `/auth/validate-invite-token?token=${encodeURIComponent(token)}`
    );

    // publicApi 인터셉터가 이미 success 체크를 했으므로 response.data.data 사용
    const userData = response.data.data;

    return {
      token,
      userData,
    };
  } catch (error: any) {
    console.error('초대 링크 검증 실패:', error);

    const errorData = error.response?.data;

    // 이미 가입된 사용자이거나 토큰이 사용 완료된 경우만 로그인으로 리다이렉트
    const isAlreadyRegistered =
      errorData?.code === 'ALREADY_REGISTERED' ||
      errorData?.message?.includes('이미 가입') ||
      errorData?.message?.includes('유효하지 않은 초대 토큰');

    if (isAlreadyRegistered) {
      return redirect('/login');
    }

    // 다른 모든 에러의 경우 register 페이지로 진행하되 에러 정보를 전달
    // 이렇게 하면 useTokenValidation hook에서 에러를 적절히 처리할 수 있음
    return {
      token,
      userData: null,
      error: errorData?.message || error.message || '토큰 검증에 실패했습니다.',
    };
  }
};

export const publicRoutes = [
  {
    path: '/login',
    element: getAuthToken()
      ? React.createElement(Navigate, { to: '/' })
      : createLazyElement(LoginPage),
  },
  {
    path: '/register',
    element: createLazyElement(RegisterPage),
    loader: registerLoader,
  },
];
